import { ChessTermType, TermAction, } from "@chesshotel/types";
import { exchangeEvaluation, uci2move, getThreats, isNegativeMove, pieceValues, } from "./util.js";
import { BLACK, Chess, swapColor, WHITE, } from "@chesshotel/chess";
function checkFollowsExchange(exchange, activeSEEs, moveIndex) {
    const { moveSequence } = exchange;
    const move = moveSequence[0];
    let follows = false;
    activeSEEs = activeSEEs.map((activeSEE) => {
        const { sequence, nextIndex } = activeSEE;
        if (sequence.length > nextIndex) {
            const newNextIndex = nextIndex + 1;
            const matchMove = sequence[newNextIndex];
            let isMatch = matchMove?.some((m) => m.to == move.to && m.from == move.from);
            if (isMatch) {
                follows = true;
                return {
                    ...activeSEE,
                    nextIndex: newNextIndex,
                    moveIndexes: [...activeSEE.moveIndexes, moveIndex],
                };
            }
        }
        return activeSEE;
    });
    return { follows, activeSEEs };
}
export function detectSEECombinations(gameReview) {
    let activeSEEs = [];
    const finalSEEs = [];
    for (let i = 1; i < gameReview.length; i++) {
        const lastPosition = gameReview[i - 1];
        const position = gameReview[i];
        const fen = lastPosition.fen;
        const chess = new Chess(fen);
        const color = chess.turn();
        const move = uci2move(position.move);
        const moveResult = chess.move(move);
        const opponentColor = chess.turn();
        const moveFromIndex = activeSEEs.findIndex((activeSEE) => {
            return activeSEE.square == move.from;
        });
        if (moveFromIndex != -1) {
            finalSEEs.push(activeSEEs[moveFromIndex]);
            activeSEEs.splice(moveFromIndex, 1);
        }
        const didCapture = !!moveResult?.captured;
        if (!didCapture) {
            if (!position.inCheck && !position.threats) {
                finalSEEs.push(...activeSEEs);
                activeSEEs = [];
            }
            continue;
        }
        const exchange = exchangeEvaluation(fen, move.to, {
            from: move.from,
            filteredByLoss: true,
        });
        if (exchange.moveSequence.length == 0) {
            continue;
        }
        const result = checkFollowsExchange(exchange, activeSEEs, position.moveIndex);
        activeSEEs = result.activeSEEs;
        if (!result.follows) {
            const activeSEE = {
                moveIndex: position.moveIndex,
                square: position.move.slice(2, 4),
                sequence: exchange.movesTree,
                nextIndex: 0,
                moveIndexes: [position.moveIndex],
                gain: exchange.gain,
                pieceWin: exchange.pieceWin,
                firstMove: moveResult,
            };
            if (exchange.moveSequence.length == 1) {
                const threats = getThreats(chess.fen(), color);
                if (threats?.length > 0) {
                    const bestThreat = threats.sort((a, b) => b.gain - a.gain)[0];
                    if (bestThreat?.gain > 0) {
                        const threatMove = {
                            from: bestThreat.from.square,
                            to: bestThreat.to.square,
                        };
                        activeSEE.sequence = [...activeSEE.sequence, [threatMove]];
                        activeSEE.gain = activeSEE.gain - bestThreat.gain;
                    }
                }
            }
            activeSEEs.push(activeSEE);
        }
    }
    if (activeSEEs.length > 0) {
        finalSEEs.push(...activeSEEs);
    }
    updateGameReview(gameReview, finalSEEs);
}
function updateGameReview(gameReview, finalSEEs) {
    for (let see of finalSEEs) {
        const { moveIndexes, sequence, gain, pieceWin, firstMove } = see;
        const firstIndex = see.moveIndex;
        const seeLength = sequence.length;
        if (seeLength == 1) {
            const position = gameReview[firstIndex];
            if (!position)
                continue;
            if (position.chessTerm) {
                continue;
            }
            const isNegative = isNegativeMove(position);
            if (isNegative) {
                continue;
            }
            let term;
            let translationKeys = {};
            if (gain > 0) {
                if (pieceValues[firstMove.captured] > pieceValues[firstMove.piece]) {
                    term = ChessTermType.DIRECT_MATERIAL_WIN;
                }
                else {
                    term = ChessTermType.HANGING;
                }
                translationKeys = { piece: pieceWin };
            }
            let arrows;
            if (Array.isArray(sequence[0])) {
                arrows = [sequence[0][0]];
            }
            else {
                arrows = [sequence[0]];
            }
            const chessTerm = {
                term,
                termAction: TermAction.PERFORM,
                move: arrows[0],
                arrows,
                translationKeys,
            };
            position.chessTerm = chessTerm;
            position.chessTerms = [chessTerm];
            continue;
        }
        for (let i = 0; i < moveIndexes.length; i++) {
            const moveIndex = moveIndexes[i];
            const position = gameReview[moveIndex];
            if (!position)
                continue;
            if (i > 0 && position.chessTerm) {
                continue;
            }
            const isNegative = isNegativeMove(position);
            let chessTerm;
            let translationKeys = {};
            if (i == 0) {
                if (position.chessTerm) {
                    continue;
                }
                let arrows = null;
                let term = null;
                let termAction = TermAction.PERFORM;
                if (gain == 0 && !isNegative) {
                    term = ChessTermType.TRADE;
                    arrows = [sequence[1][0]];
                }
                if (gain > 0 && !isNegative) {
                    term = ChessTermType.MATERIAL_WIN;
                    translationKeys = { piece: pieceWin };
                }
                if (gain > 0 && isNegative) {
                    if (sequence.length == 2) {
                        term = ChessTermType.BAD_TRADE;
                        arrows = [sequence[1][0]];
                    }
                    else {
                        continue;
                    }
                }
                if (gain == 0 && isNegative) {
                    term = ChessTermType.BAD_EQUAL_TRADE;
                    arrows = [sequence[1][0]];
                }
                if (gain < 0 && isNegative) {
                    term = ChessTermType.MATERIAL_WIN;
                    termAction = TermAction.ALLOW;
                    translationKeys = { piece: pieceWin };
                }
                if (gain < 0 && !isNegative) {
                    continue;
                }
                chessTerm = {
                    term,
                    termAction,
                    translationKeys: {
                        ...translationKeys,
                        color: moveIndex % 2 == 1 ? WHITE : BLACK,
                    },
                    arrows,
                    move: sequence[0][0],
                    moveTree: sequence,
                };
            }
            if (i > 0) {
                const term = i % 2 == 1 ? ChessTermType.RETAKE : ChessTermType.TAKES_BACK;
                chessTerm = {
                    term,
                    termAction: TermAction.PERFORM,
                };
            }
            position.chessTerm = chessTerm;
            position.chessTerms = [chessTerm];
        }
    }
}
export function detectAllThreats(gameReview) {
    for (let i = 1; i < gameReview.length; i++) {
        const position = gameReview[i];
        const chess = new Chess(position.fen);
        const opponentColor = chess.turn();
        const color = swapColor(opponentColor);
        if (chess.inCheck()) {
            const kingSquare = chess.getKing(opponentColor);
            const king = chess.getBoardPiece(kingSquare);
            const attackers = chess.attackers(kingSquare, color);
            const checkingPieces = attackers.map((square) => {
                const from = chess.getBoardPiece(square);
                return { from, to: king };
            });
            if (checkingPieces.length > 0) {
                position.inCheck = checkingPieces[0];
            }
        }
        else {
            chess.changeTurn();
            const fen = chess.fen();
            const threats = getThreats(fen, opponentColor);
            if (threats.length > 0) {
                position.threats = threats;
            }
        }
    }
}
//# sourceMappingURL=combinations.js.map